/**
 * MusicModelSelector — thin wrapper around ProviderPicker.
 *
 * Maps MusicModel[] from the API to PickerOption[], marks Lyria 3 as
 * recommended, and delegates layout / collapsed-preview to ProviderPicker.
 */

import { memo, useMemo } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import type { MusicModel } from '../../services/musicGenerationAPI';
import { ProviderPicker } from '../../components/common/ProviderPicker';
import type { PickerOption } from '../../components/common/ProviderPicker';

interface MusicModelSelectorProps {
  models: MusicModel[];
  selectedModel: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
}

export const MusicModelSelector = memo(function MusicModelSelector({
  models,
  selectedModel,
  onChange,
  disabled = false,
}: MusicModelSelectorProps) {
  const { t } = useTranslation();

  const options = useMemo<PickerOption[]>(() => {
    // When no models have been fetched yet, show a placeholder Lyria Clip entry.
    const list: MusicModel[] =
      models.length > 0
        ? models
        : [
            {
              id: 'lyria-clip',
              display_name: 'Lyria Clip',
              description: 'Google Lyria 3 music generation',
              max_duration_seconds: 30,
              cost_usd: 0,
              estimated_tokens: 0,
              supports_lyrics: false,
              supports_vocals: false,
              provider: 'lyria',
            },
          ];

    return list.map((m) => ({
      value: m.id,
      label: m.display_name,
      description: m.description || undefined,
      // Mark Lyria as recommended (lyria-clip is always the first / only entry
      // when models are empty; for real responses it matches by prefix).
      recommended: m.id === 'lyria-clip' || m.id.startsWith('lyria'),
    }));
  }, [models]);

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-white/70">
        {t('music.form.modelLabel', { defaultValue: 'Model' })}
      </label>

      <ProviderPicker
        options={options}
        value={selectedModel}
        onChange={onChange}
        sortKeys={['name']}
        defaultSortKey="name"
        disabled={disabled}
      />
    </div>
  );
});

export default MusicModelSelector;
